/**
 * @file LabelPrintLogSheet.tsx
 * @description Шторка «История печати» (phase19.22, ui-design §1):
 *   карточки журнала с [Открыть] и [Перепечатать]. На `<sm` — на весь экран.
 *
 * Данные — узкий контракт `LabelPrintLogData`: его отдаёт и журнал приёмки
 * (`useReceiptLabels`), и журнал этикеток ячеек (`useBinLabelJobs`, без
 * строк документа — phase19.22 шаг 5). Нет `reprint` — нет и кнопки; нет
 * `hideFile` — нет пункта «Скрыть файл» в «⋯».
 */

import { Loader2 } from "lucide-react";

import { LabelPrintLog } from "./LabelPrintLog";
import { LABEL_TEXT } from "./label-texts";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import type { LabelPrintJobDto } from "../../../lib/api-labels";

/** Что нужно шторке от источника журнала. */
export interface LabelPrintLogData {
  jobs: LabelPrintJobDto[];
  jobsLoading: boolean;
  reprintingId?: string | null;
  /** Нет — перепечатки в этом журнале нет. */
  reprint?: (job: LabelPrintJobDto) => Promise<void> | void;
  /** «Скрыть файл»: `true` — скрыт. Нет — пункта в «⋯» нет. */
  hideFile?: (job: LabelPrintJobDto) => Promise<boolean>;
  hidingId?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labels: LabelPrintLogData;
  title?: string;
  /** false — без строк документа (журнал ячеек). */
  showLines?: boolean;
  emptyText?: string;
}

export function LabelPrintLogSheet({ open, onOpenChange, labels, title, showLines, emptyText }: Props) {
  const reprint = labels.reprint;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-none overflow-y-auto sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{title ?? LABEL_TEXT.historyTitle}</SheetTitle>
        </SheetHeader>
        {labels.jobsLoading && labels.jobs.length === 0 ? (
          <div className="flex justify-center py-8">
            <Loader2 className="size-5 animate-spin text-primary" />
          </div>
        ) : (
          <LabelPrintLog
            jobs={labels.jobs}
            reprintingId={labels.reprintingId}
            onReprint={reprint ? (job) => void reprint(job) : undefined}
            onHide={labels.hideFile}
            hidingId={labels.hidingId}
            showLines={showLines}
            emptyText={emptyText}
            layout="cards"
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
